import React from 'react';
import { cn } from '@/lib/utils';

/**
 * CeleyLoader — branded, CSS-only loading experience.
 *
 * Replaces the generic "Loading..." text on full-page bootstrap surfaces
 * (App protected-route gate, VendorDetail page load, CustomerDashboard
 * initial fetch, PlatformSettings initial fetch). Designed to feel calm
 * and premium, not playful: a Celey balloon mark that drifts up-down
 * with a soft pulse, on a clean white surface.
 *
 * Architecture rules respected:
 *   - CSS-only animation (keyframes inline; no Motion / Lottie / Tailwind
 *     animate-* plugin dependency).
 *   - Respects `prefers-reduced-motion` — the drift + pulse become a
 *     static glyph for users who request reduced motion.
 *   - Does NOT replace button spinners or any inline `animate-spin`
 *     operational feedback. Those continue to communicate action state.
 *
 * Two modes:
 *   <CeleyLoader />                fullscreen centered (default)
 *   <CeleyLoader inline />         inline auto-sized for mid-list use
 *
 * Props:
 *   label?     — small caption under the balloon (default: "Loading")
 *   inline?    — boolean; render without the min-h-screen wrapper
 *   size?      — 'sm' | 'md' | 'lg' (default 'md')
 *   testid?    — preserved data-testid (default 'celey-loader')
 */
const SIZE_MAP = {
  sm: { svg: 28, label: 'text-[11px]' },
  md: { svg: 56, label: 'text-xs' },
  lg: { svg: 80, label: 'text-sm' },
};

const BalloonMark = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Soft glow behind the balloon */}
    <ellipse cx="32" cy="28" rx="22" ry="22" fill="#10b981" opacity="0.08" />
    {/* Balloon body — emerald gradient */}
    <defs>
      <linearGradient id="celey-balloon-grad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#34d399" />
        <stop offset="100%" stopColor="#059669" />
      </linearGradient>
    </defs>
    <path
      d="M32 8c8.84 0 16 7.16 16 16 0 9.94-9.6 18.5-15.06 22.41a1.6 1.6 0 0 1-1.88 0C25.6 42.5 16 33.94 16 24c0-8.84 7.16-16 16-16Z"
      fill="url(#celey-balloon-grad)"
    />
    {/* Highlight on the balloon */}
    <ellipse cx="26" cy="18" rx="3" ry="5" fill="white" opacity="0.35" />
    {/* Knot */}
    <path d="M30 46.5 32 50l2-3.5h-4Z" fill="#047857" />
    {/* String */}
    <path
      d="M32 50 30.5 60M32 50 33.5 60"
      stroke="#9ca3af"
      strokeWidth="1"
      strokeLinecap="round"
    />
  </svg>
);

export const CeleyLoader = ({
  label = 'Loading',
  inline = false,
  size = 'md',
  testid = 'celey-loader',
  className,
}) => {
  const { svg, label: labelSize } = SIZE_MAP[size] || SIZE_MAP.md;

  // Inline styles for the keyframes so the loader is self-contained and
  // works in any consuming surface without depending on global CSS.
  const styleTag = (
    <style>{`
      @keyframes celey-loader-drift {
        0%, 100% { transform: translateY(0); }
        50%      { transform: translateY(-6px); }
      }
      @keyframes celey-loader-pulse {
        0%, 100% { opacity: 0.85; }
        50%      { opacity: 1; }
      }
      @keyframes celey-loader-dots {
        0%, 20%  { opacity: 0; }
        50%      { opacity: 1; }
        80%, 100%{ opacity: 0; }
      }
      .celey-loader-drift {
        animation: celey-loader-drift 2.2s ease-in-out infinite,
                   celey-loader-pulse 2.2s ease-in-out infinite;
      }
      .celey-loader-dot {
        animation: celey-loader-dots 1.4s ease-in-out infinite;
      }
      .celey-loader-dot:nth-child(2) { animation-delay: 0.15s; }
      .celey-loader-dot:nth-child(3) { animation-delay: 0.3s; }
      @media (prefers-reduced-motion: reduce) {
        .celey-loader-drift,
        .celey-loader-dot { animation: none; }
      }
    `}</style>
  );

  const core = (
    <div
      className={cn('flex flex-col items-center justify-center gap-3', className)}
      data-testid={testid}
    >
      {styleTag}
      <div className="celey-loader-drift" style={{ willChange: 'transform, opacity' }}>
        <BalloonMark size={svg} />
      </div>
      {label && (
        <div className={cn('font-medium text-gray-500 flex items-center', labelSize)}>
          <span>{label}</span>
          <span className="celey-loader-dot ml-0.5">.</span>
          <span className="celey-loader-dot">.</span>
          <span className="celey-loader-dot">.</span>
        </div>
      )}
    </div>
  );

  if (inline) return core;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F7F5]">
      {core}
    </div>
  );
};

export default CeleyLoader;
